import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Popconfirm,
  Tree,
  Row,
  Col,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { roleAPI } from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;

const RoleList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [permissionModalVisible, setPermissionModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [checkedPermissions, setCheckedPermissions] = useState([]);
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();

  useEffect(() => {
    loadData();
    loadPermissions();
  }, [page, pageSize, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await roleAPI.getList({ page, pageSize, ...filters });
      if (res.data?.success) {
        setData(res.data.data.list);
        setTotal(res.data.data.total);
      }
    } catch (error) {
      message.error('加载角色列表失败');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const res = await roleAPI.getPermissions();
      if (res.data?.success) {
        setPermissions(res.data.data);
      }
    } catch (error) {
      console.error('加载权限列表失败:', error);
    }
  };

  const handleSearch = (values) => {
    setFilters(values);
    setPage(1);
  };

  const handleReset = () => {
    setFilters({});
    setPage(1);
  };

  const handleAdd = () => {
    setEditingRole(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRole(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await roleAPI.delete(id);
      if (res.data?.success) {
        message.success('删除成功');
        loadData();
      } else {
        message.error(res.data?.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let res;

      if (editingRole) {
        res = await roleAPI.update(editingRole.id, values);
      } else {
        res = await roleAPI.create(values);
      }

      if (res.data?.success) {
        message.success(editingRole ? '更新成功' : '创建成功');
        setModalVisible(false);
        loadData();
      } else {
        message.error(res.data?.message || '操作失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
    }
  };

  const handleEditPermissions = (record) => {
    setEditingRole(record);
    setCheckedPermissions(record.permissions || []);
    setPermissionModalVisible(true);
  };

  const handlePermissionSubmit = async () => {
    try {
      const res = await roleAPI.update(editingRole.id, {
        ...editingRole,
        permissions: checkedPermissions
      });

      if (res.data?.success) {
        message.success('权限更新成功');
        setPermissionModalVisible(false);
        loadData();
      } else {
        message.error(res.data?.message || '更新失败');
      }
    } catch (error) {
      console.error('更新权限失败:', error);
    }
  };

  // 构建权限树数据
  const buildPermissionTree = () => {
    return Object.entries(permissions).map(([module, perms]) => ({
      title: module,
      key: `module_${module}`,
      children: perms.map(perm => ({
        title: perm.name,
        key: perm.code
      }))
    }));
  };

  const handlePermissionCheck = (checkedKeys) => {
    // 过滤掉模块节点，只保留权限节点
    const permKeys = checkedKeys.filter(key => !key.startsWith('module_'));
    setCheckedPermissions(permKeys);
  };

  const handleInitRoles = async () => {
    try {
      const res = await roleAPI.initRoles();
      if (res.data?.success) {
        message.success('初始化默认角色成功');
        loadData();
      } else {
        message.error(res.data?.message || '初始化失败');
      }
    } catch (error) {
      message.error('初始化失败');
    }
  };

  const columns = [
    {
      title: '角色名称',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '角色编码',
      dataIndex: 'code',
      key: 'code',
      width: 120
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '权限数',
      key: 'permissionCount',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Tag color="blue">{record.permissions?.length || 0}</Tag>
      )
    },
    {
      title: '类型',
      dataIndex: 'isSystem',
      key: 'isSystem',
      width: 80,
      render: (isSystem) => (
        <Tag color={isSystem ? 'purple' : 'default'}>
          {isSystem ? '系统' : '自定义'}
        </Tag>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val) => val ? new Date(val).toLocaleString() : '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<SafetyCertificateOutlined />}
            onClick={() => handleEditPermissions(record)}
          >
            权限
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {!record.isSystem && (
            <Popconfirm
              title="确定要删除该角色吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Form layout="inline" onFinish={handleSearch}>
          <Form.Item name="keyword">
            <Input placeholder="角色名称/编码" allowClear style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="状态" allowClear style={{ width: 100 }}>
              <Option value="active">启用</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card
        title="角色管理"
        extra={
          <Space>
            <Button onClick={handleInitRoles}>
              初始化默认角色
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增角色
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1000 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            }
          }}
        />
      </Card>

      {/* 新增/编辑角色弹窗 */}
      <Modal
        title={editingRole ? '编辑角色' : '新增角色'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={500}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="角色名称"
            rules={[{ required: true, message: '请输入角色名称' }]}
          >
            <Input placeholder="请输入角色名称" />
          </Form.Item>
          <Form.Item
            name="code"
            label="角色编码"
            rules={[{ required: true, message: '请输入角色编码' }]}
          >
            <Input
              placeholder="请输入角色编码"
              disabled={editingRole?.isSystem}
            />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="请输入描述" />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="active">
            <Select>
              <Option value="active">启用</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 权限配置弹窗 */}
      <Modal
        title={`配置权限 - ${editingRole?.name || ''}`}
        open={permissionModalVisible}
        onOk={handlePermissionSubmit}
        onCancel={() => setPermissionModalVisible(false)}
        width={600}
      >
        <div style={{ maxHeight: 400, overflow: 'auto' }}>
          <Tree
            checkable
            defaultExpandAll
            checkedKeys={checkedPermissions}
            onCheck={handlePermissionCheck}
            treeData={buildPermissionTree()}
          />
        </div>
        <Divider />
        <div>
          已选择 <Tag color="blue">{checkedPermissions.length}</Tag> 项权限
        </div>
      </Modal>
    </div>
  );
};

export default RoleList;
